import { StarIcon } from "@/icons";
import { cn } from "@/lib/cn";
import { forwardRef, useRef, type KeyboardEvent } from "react";
import { Icon, type IconSize } from "./Icon";

type RatingSize = "sm" | "md" | "lg";

const GLYPH: Record<RatingSize, IconSize> = { sm: "xs", md: "md", lg: "3xl" };
const BUTTON: Record<RatingSize, string> = { sm: "h-6 w-6", md: "h-8 w-8", lg: "h-12 w-12" };
const GAP: Record<RatingSize, string> = { sm: "gap-0.5", md: "gap-1", lg: "gap-1" };

/** Props of `RatingStars`; with `onChange` it becomes a radio group, otherwise a read-only image. */
export type RatingStarsProps = {
  value: number;
  max?: number;
  size?: RatingSize;
  label?: string;
  onChange?: (value: number) => void;
  starLabel?: (stars: number) => string;
  className?: string;
};

const defaultStarLabel = (stars: number) => (stars === 1 ? "1 estrela" : `${stars} estrelas`);

function Star({ filled, size }: { filled: boolean; size: RatingSize }) {
  return (
    <Icon
      as={StarIcon}
      size={GLYPH[size]}
      weight={filled ? "fill" : "regular"}
      className={cn("transition-colors duration-fast ease-standard", filled ? "text-primary" : "text-gray-scale-300")}
    />
  );
}

/** Star rating: read-only display or an arrow-key operable radio group of star buttons. */
export const RatingStars = forwardRef<HTMLDivElement, RatingStarsProps>(function RatingStars(
  { value, max = 5, size = "md", label, onChange, starLabel = defaultStarLabel, className },
  ref,
) {
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const stars = Array.from({ length: max }, (_, index) => index + 1);

  if (!onChange) {
    return (
      <div
        ref={ref}
        role="img"
        aria-label={label ?? `Nota ${value} de ${max}`}
        className={cn("inline-flex items-center", GAP[size], className)}
      >
        {stars.map((n) => (
          <Star key={n} filled={n <= Math.round(value)} size={size} />
        ))}
      </div>
    );
  }

  const select = (next: number) => {
    const clamped = Math.min(max, Math.max(1, next));
    onChange(clamped);
    buttons.current[clamped - 1]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const moves: Record<string, number> = {
      ArrowRight: (value || 0) + 1,
      ArrowUp: (value || 0) + 1,
      ArrowLeft: (value || 2) - 1,
      ArrowDown: (value || 2) - 1,
      Home: 1,
      End: max,
    };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    select(next);
  };

  return (
    <div ref={ref} role="radiogroup" aria-label={label} className={cn("flex items-center", GAP[size], className)}>
      {stars.map((n) => (
        <button
          key={n}
          ref={(node) => {
            buttons.current[n - 1] = node;
          }}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={starLabel(n)}
          tabIndex={value === n || (value === 0 && n === 1) ? 0 : -1}
          onClick={() => onChange(n)}
          onKeyDown={onKeyDown}
          className={cn(
            "grid place-items-center rounded-control transition-transform duration-fast ease-standard active:scale-90",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            BUTTON[size],
          )}
        >
          <Star filled={n <= value} size={size} />
        </button>
      ))}
    </div>
  );
});
